import React, { useState } from 'react';
import { array, bool, func, object, string, number } from 'prop-types';

import { connect } from 'react-redux';

import { get } from 'lodash';

import moment from 'moment';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';

import AutoComplete from '../../components/FormFields/Autocomplete';
import DateField from '../../components/FormFields/DateField';
import EsmsDialog from '../../components/EsmsDialog';

import * as ServiceManagementActions from '../../actions/serviceManagementActions';
import * as UiActions from '../../actions/uiActions';

import classes from './AppointmentModal.module.css';
import 'react-datepicker/dist/react-datepicker.css';

const ValidationSchema = Yup.object().shape({
    primaryTechnician: Yup.string()
        .required(),
    scheduledDateTime: Yup.string()
        .required(),
    appointmentStatus: Yup.string()
        .required(),
    technicianNotes: Yup.string()
});

const DEFAULT_FORM_VALUES = {
    primaryTechnician: '',
    appointmentStatus: '',
    technicianNotes:   ''
};

const InitialValues = {...DEFAULT_FORM_VALUES};

const ManageAppointmentModal = ({
    activeItem,
    appointmentStatuses,
    close,
    open,
    toggleLoading,
    clearErrors,
    dateFormat,
    timeFormat,
    timeIntervals,
    showTimeSelect,
    isLoading,
    technicianList,
    title,
    ...props
}) => {
    const [ sendComms, setSendComms ] = useState(false);
    const [ additionalTechs, setAdditionalTechs ] = useState([]);

    const formik = useFormik({
        initialValues:      InitialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                activeAppointment,
                updateAppointment
            } = props;

            let isUpdated;

            const body = {
                primaryTechnicianId: values.primaryTechnician.id,
                scheduledDateTime:   moment(values.scheduledDateTime).toISOString(),
                serviceCallId:       get(activeItem, 'id', ''),
                statusId:            values.appointmentStatus.id,
                otherTechnicianIds:  additionalTechs.map(tech => tech.id),
                technicianNotes:     values.technicianNotes,
                id:                  activeAppointment.id
            };

            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isUpdated = await updateAppointment(body, {notify: sendComms});
                }

                if (isUpdated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const handleReset = async () => {
        const { activeAppointment } = props;

        if (InitialValues.primaryTechnician.length) {
            Object.assign(InitialValues, DEFAULT_FORM_VALUES);
        }

        const primaryTechnician = technicianList[0].items.find(tech => tech.id === activeAppointment.primaryTechnicianId);
        const appointmentStatus = appointmentStatuses[0].items.find(status => status.id === activeAppointment.statusId);

        InitialValues.primaryTechnician = primaryTechnician;
        InitialValues.scheduledDateTime = new Date(activeAppointment.scheduledDateTime);
        InitialValues.appointmentStatus = appointmentStatus;
        InitialValues.technicianNotes = activeAppointment.technicianNotes;

        if (activeAppointment.otherTechnicianIds) {
            activeAppointment.otherTechnicianIds.forEach(id => {
                const tech = technicianList[0].items.find(tech => tech.id === id);

                if (tech) {
                    setAdditionalTechs([...additionalTechs, tech]);
                }
            });
        }

        setSendComms(false);

        formik.handleReset(InitialValues);
    };

    const handleClose = () => {
        close();
    };

    const handleChangeTime = (name) => {
        return (value, event) => {
            if (value) {
                formik.setFieldValue(name, value);
            }
            formik.setFieldTouched(name, true);
        };
    };

    const handleToggleAdditionalTechs = tech => {
        const techExists = additionalTechs.some(addTech => addTech.id === tech.id);

        if (techExists) {
            let updatedTechs = additionalTechs.filter(addTech => addTech.id !== tech.id);

            setAdditionalTechs(updatedTechs);
            return;
        }

        setAdditionalTechs([...additionalTechs, tech]);
    };

    const handleSendComms = () => {
        setSendComms(!sendComms);
    };

    if (!technicianList.length) return null;

    const filteredTechnicians = technicianList[0].items.filter(technician => technician.isActive === true);

    const filteredTechniciansList = [
        {
            items:     [...filteredTechnicians],
            name:      'Technicians',
            itemProps: {
                type: 'technicians'
            }
        }
    ];

    return (
        <EsmsDialog
            open={open}
            onClose={handleClose}
            title={title}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting || activeItem.locked}
            onRendered={handleReset}
            isLoading={isLoading}
            confirmText="Update"
            className={classes.AppointmentModal}
        >
            <div className={classes.AppointmentModalWrapper}>
                <div className={classes.FormContainer}>
                    <FormControl className={classes.FullWidth}>
                        <AutoComplete
                            grouped
                            label="Primary Technician"
                            name="primaryTechnician"
                            classes={{
                                inputRoot: classes.inputRoot
                            }}
                            onChange={formik.handleChange}
                            className={classes.autoComplete}
                            options={filteredTechniciansList}
                            value={formik.values.primaryTechnician}
                            error={Boolean(formik.errors.primaryTechnician) && Boolean(formik.touched.primaryTechnician)}
                            helperText={Boolean(formik.touched.primaryTechnician) && formik.errors.primaryTechnician}
                            disabled={activeItem.locked}
                        />
                    </FormControl>
                    <FormControl className={classes.FullWidth}>
                        <div className={classes.DatePicker}>
                            <DateField
                                label="Schedule Date/Time"
                                onChange={handleChangeTime('scheduledDateTime')}
                                margin="dense"
                                name="appointmentTimestamp"
                                dateFormat={dateFormat}
                                showTimeSelect={showTimeSelect}
                                timeFormat={timeFormat}
                                value={formik.values.scheduledDateTime}
                                disabled={activeItem.locked}
                            />
                        </div>
                    </FormControl>
                    <FormControl className={classes.FullWidth}>
                        <AutoComplete
                            grouped
                            label="Appointment Status"
                            name="appointmentStatus"
                            classes={{
                                inputRoot: classes.inputRoot
                            }}
                            onChange={formik.handleChange}
                            className={classes.autoComplete}
                            options={appointmentStatuses}
                            value={formik.values.appointmentStatus}
                            error={Boolean(formik.errors.appointmentStatus) && Boolean(formik.touched.appointmentStatus)}
                            helperText={Boolean(formik.touched.appointmentStatus) && formik.errors.appointmentStatus}
                            disabled={activeItem.locked}
                        />
                    </FormControl>
                </div>
                <div className={classes.TechnicianContainer}>
                    <div className={classes.ContainerLabel}>
                        Additional Technicians
                    </div>
                    <div className={classes.TechnicianList}>
                        <ul className={classes.List}>
                            {
                                filteredTechniciansList[0].items.map((technician, index) =>
                                    <MenuItem
                                        key={index}
                                    >
                                        <Checkbox
                                            classes={{
                                                root: classes.checkbox
                                            }}
                                            color="primary"
                                            onClick={() => handleToggleAdditionalTechs(technician)}
                                            checked={additionalTechs.some(tech => tech.id === technician.id)}
                                            disabled={activeItem.locked}
                                        />
                                        <span className={classes.TechnicianNames}>{technician.name}</span>
                                    </MenuItem>
                                )
                            }
                        </ul>
                    </div>
                </div>
                <div className={classes.NotesContainer}>
                    <div className={classes.ContainerLabel}>
                        Technician Notes
                    </div>
                    <FormControl className={classes.FullWidth}>
                        <TextField
                            multiline
                            rows={8}
                            value={formik.values.technicianNotes}
                            onChange={formik.handleChange}
                            name="technicianNotes"
                            className={classes.Comments}
                            disabled={activeItem.locked}
                            variant="outlined"
                        />
                    </FormControl>
                </div>
            </div>
            <div className={classes.SendCommsContainer}>
                <FormControl className={classes.checkboxWrapper}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={sendComms}
                                onChange={handleSendComms}
                                name="sendCommunications"
                                color="primary"
                                disabled={activeItem.locked}
                            />
                        }
                        label="Send Client communications on Update (if applicable)"
                        className={classes.checkboxLabel}
                    />
                </FormControl>
            </div>
        </EsmsDialog>
    );
};

ManageAppointmentModal.defaultProps = {
    activeItem:          {},
    activeAppointment:   {},
    appointmentStatuses: [],
    close:               () => {},
    dateFormat:          'yyyy-MM-dd h:mm a',
    isLoading:           false,
    open:                false,
    showTimeSelect:      true,
    technicianList:      [],
    timeFormat:          'h:mm a',
    timeIntervals:       15,
    title:               ''
};

ManageAppointmentModal.propTypes = {
    activeItem:          object,
    activeAppointment:   object,
    appointmentStatuses: array,
    close:               func,
    open:                bool,
    dateFormat:          string,
    isLoading:           bool,
    showTimeSelect:      bool,
    technicianList:      array,
    timeFormat:          string,
    timeIntervals:       number,
    title:               string
};

const mapStateToProps = state => {
    return {
        activeItem:          state.svcManagement.activeItem,
        activeAppointment:   state.svcManagement.activeAppointment,
        appointmentStatuses: state.svcManagement.appointmentStatuses,
        isLoading:           state.ui.isLoading,
        technicianList:      state.svcManagement.technicianList
    };
};

const mapDispatchToProps = {
    ...ServiceManagementActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageAppointmentModal);
